import React, {FC, memo, useCallback, useContext, useMemo, useRef} from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {useBoostExpediaBackgroundTabHotelCard} from 'components/BoostExpedia'
import {CallToBookAnchor} from 'components/CallToBook/CallToBookAnchor'
import {PartialFiltersMatch} from 'components/HotelCard/PartialFiltersMatch/PartialFiltersMatch'
import {HotelCardContext} from 'components/HotelCardContext'
import {AnchorLabel} from 'components/HotelCardDesktop/AnchorLabel'
import {HistoricalPriceOffer} from 'components/Offer/HistoricalPriceOffer'
import {getOfferVariant} from 'components/Offer/Shared/utils'
import {OfferBox} from 'components/OfferBox/OfferBox'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'
import {usePropertyHighlightsAnalyticsPayload} from 'components/PropertyHighlights'
import {HotelMoreDetails} from 'components/SeoSRP/HotelMoreDetails/HotelMoreDetails'
import {UnavailabilityMobile} from 'components/Unavailability/Mobile'
import {VacationRentalDetails} from 'components/VacationRentalDetails'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {useHotelCardActions} from 'hooks/useHotelCardActions'
import {useHotelCardAlternativeClick} from 'hooks/useHotelCardAlternativeClick'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {useOfferClickHandler} from 'hooks/useOfferClickHandler'
import {useOtaMode} from 'hooks/useOtaMode'
import useRedirectLink from 'hooks/useRedirectLink'
import {useShouldShowDealFreezeBanner} from 'hooks/useShouldShowDealFreezeBanner'
import {useShowAccommodationPage} from 'hooks/useShowAccommodationPage'
import {
  getHotel,
  getIsSearchPending,
  getOfferAccessFlowSettings
} from 'modules/sapiSearch/selectors'
import {getHotelTopOffer, getOfferEntity} from 'modules/sapiSearch/selectors'
import {getListOfValues} from 'modules/search/selectors'
import {getRankedAvailableFacilities} from 'utils/facilities'
import {SelectedElement} from 'utils/selectedElement'

import {Box} from '@vio/atlas/helpers/Box'
import {NoStyleLink} from '@vio/atlas/src/components/designSystem/helpers/NoStyleLink'
import {cssTheme} from '@vio/atlas/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {getClosestDataIdToEventTarget} from '@vio/core/src/_web/utils/browser'
import {isAirbnbOffer} from '@vio/core/src/offer/business/isAirbnbOffer'
import {OfferVariant} from '@vio/core/src/offer/types/OfferVariant'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {PriceWatchBell} from '@vio/shared/src/priceWatch/components/PriceWatchBell'

import {GalleryWithFetchPriority} from '../Gallery/GalleryWithFetchPriority'
import {HotelCardLayout} from '../HotelCardLayout/HotelCardLayout'
import {PropertySummary} from '../PropertySummary/PropertySummary'
import {HotelCardDealFreezeBanner} from './HotelCardDealFreezeBanner'

const AvailabilityPaneWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s400} 0;
  border-radius: ${cssTheme.layout.radius.md};
  border: 1px solid;
  border-color: ${cssTheme.colors.border.brand.tertiary};
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
  overflow: hidden;
  &.--special-border {
    border-color: ${cssTheme.colors.border.brand.tertiary};
  }
`

const SOURCE_COMPONENT = 'HotelCardMobile'

// Calls the heavy useOfferClickHandler hook only for BNB cards and exposes the
// handler to the parent via a ref, so non-BNB cards skip the work.
const AirbnbClickHandlerSetter: FC<{
  hotelId: string
  topOffer: SearchOffer
  handlerRef: React.MutableRefObject<
    ((e: React.MouseEvent) => void) | undefined
  >
}> = ({hotelId, topOffer, handlerRef}) => {
  const {handleClick} = useOfferClickHandler({
    component: 'HotelCardNonOffer',
    hotelId,
    offer: topOffer,
    offerPosition: 0
  })
  handlerRef.current = handleClick
  return null
}

/**
 * Component responsible for composing components into `HotelCardLayout` slots
 */
const ComposedHotelCardComponent: FC<{isPriorityCard: boolean}> = ({
  isPriorityCard
}) => {
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const {hotelDetailsIsOpenOnLanding, hotelId, isAnchor} =
    useContext(HotelCardContext)
  // Added as part of dcbc5616-property-highlights-v1
  const {highlights} = usePropertyHighlightsAnalyticsPayload(hotelId)

  const hotel = useSelector(state => getHotel(state, hotelId))

  const isSeoSrp = useIsSeoSRP()

  const listOfValues = useSelector(getListOfValues)
  const rankedFacilities = getRankedAvailableFacilities(
    hotel?.facilities,
    listOfValues
  )

  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))

  const topOffer = useSelector(state => getHotelTopOffer(state, hotelId))
  const isSearchPending = useSelector(getIsSearchPending)

  const {isAppRequired} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offerEntity?.id)
  )
  const {isOTAMode} = useOtaMode()
  const anchorPriceTotal = offerEntity?.anchorPriceRateBreakdown?.totalRate ?? 0
  const hydrated = useIsHydrated()
  const redirectUrl = useRedirectLink(
    {offer: topOffer, hotelId, anchorPriceTotal},
    hydrated
  )
  const shouldRedirectToAirbnb = isAirbnbOffer(topOffer) && Boolean(redirectUrl)
  const airbnbClickHandlerRef = useRef<
    ((e: React.MouseEvent) => void) | undefined
  >()
  const isEmbedded = useIsEmbeddedWithBrand()

  const {
    monetisation: {cashback}
  } = useBrandCapabilities()

  const topOfferVariant =
    topOffer &&
    getOfferVariant({
      isCheapest: false
    })

  const isTopOfferMemberDeal =
    topOfferVariant === OfferVariant.MemberDeal || isAppRequired

  // Added as a part of c747927f-deal-freeze-srp.
  const shouldShowDealFreezeBanner = useShouldShowDealFreezeBanner({
    hotelId
  })

  const basicPropertyDetailsComponent = useMemo(() => {
    if (hotelDetailsIsOpenOnLanding && isAnchor) {
      return (
        <Box padding="s400">
          <VacationRentalDetails
            showVrDetails={hotel?.isVr}
            vrDetails={hotel?.vrDetails}
            rankedFacilities={rankedFacilities}
          />
        </Box>
      )
    }

    if (hotel && isSeoSrp) {
      return <HotelMoreDetails hotel={hotel} fullwidth={true} />
    }

    return null
  }, [hotel, hotelDetailsIsOpenOnLanding, isAnchor, rankedFacilities, isSeoSrp])

  const unavailabilityComponent = useMemo(
    () => (
      <AvailabilityPaneWrapper
        className={cx(isTopOfferMemberDeal && `--special-border`)}
      >
        <UnavailabilityMobile
          hotelId={hotelId}
          sourceComponentPrefix={SOURCE_COMPONENT}
        />
      </AvailabilityPaneWrapper>
    ),
    [hotelId, isTopOfferMemberDeal]
  )

  const filtersMatchSlot = useMemo(
    () =>
      !isAnchor && (
        <PartialFiltersMatch
          hasBasicPropertyDetails={hotelDetailsIsOpenOnLanding && isAnchor}
        />
      ),
    [hotelDetailsIsOpenOnLanding, isAnchor]
  )

  const {onShowHotelDetails, trackUserHotelCardClicked} = useHotelCardActions()
  const handleAlternativeClick = useHotelCardAlternativeClick({
    hotelId,
    component: 'ListViewHotelCard',
    highlights
  })
  const {getAccommodationPageUrl} = useShowAccommodationPage()
  const accommodationPageURL = getAccommodationPageUrl(hotelId)

  const handleOnShowHotelDetails = useCallback(
    // Added as a part of c747927f-deal-freeze-srp
    (showDealFreezeToast = false) => {
      onShowHotelDetails(hotelId, showDealFreezeToast)
    },
    [onShowHotelDetails, hotelId]
  )

  // Added as part of 2a7f5ff0-expedia-back-tab
  const {forceClickHandler, enhancedOnShowHotelDetails} =
    useBoostExpediaBackgroundTabHotelCard({
      hotelId,
      hasOtaRedirect: Boolean(isOTAMode && redirectUrl),
      originalOnShowHotelDetails: handleOnShowHotelDetails
    })

  const shouldUseAnchorLink = shouldRedirectToAirbnb || !forceClickHandler
  const anchorRef = useRef<HTMLAnchorElement>(null)

  const handleHotelCardClick = useCallback(
    (e: React.MouseEvent) => {
      trackUserHotelCardClicked({
        hotelId: hotelId,
        component: 'ListViewHotelCard',
        selectedElement:
          getClosestDataIdToEventTarget(e) || SelectedElement.BlankSpace,
        // Added as part of dcbc5616-property-highlights-v1
        highlights
      })

      if (shouldRedirectToAirbnb) {
        // Also emit the offer-click + ProviderRedirect tracking for BNB cards.
        airbnbClickHandlerRef.current?.(e)
      }

      // Let the anchor handle navigation
      if (anchorRef.current?.contains(e.target as Node)) return

      if (shouldRedirectToAirbnb || (isOTAMode && redirectUrl)) {
        window.open(redirectUrl, isEmbedded ? '_self' : '_blank')
        return
      }

      enhancedOnShowHotelDetails()
    },
    [
      shouldRedirectToAirbnb,
      trackUserHotelCardClicked,
      hotelId,
      highlights,
      isOTAMode,
      redirectUrl,
      enhancedOnShowHotelDetails,
      isEmbedded
    ]
  )

  // Added as a part of c747927f-deal-freeze-srp.
  const handleDealFreezeBannerClick = useCallback(
    (e: React.MouseEvent) => {
      trackUserHotelCardClicked({
        hotelId,
        component: 'ListViewHotelCard',
        selectedElement:
          getClosestDataIdToEventTarget(e) || SelectedElement.DealFreezeBanner,
        // Added as part of dcbc5616-property-highlights-v1
        highlights
      })

      handleOnShowHotelDetails(true)
    },
    [trackUserHotelCardClicked, hotelId, highlights, handleOnShowHotelDetails]
  )

  return (
    <>
      {shouldRedirectToAirbnb && topOffer && (
        <AirbnbClickHandlerSetter
          hotelId={hotelId}
          topOffer={topOffer}
          handlerRef={airbnbClickHandlerRef}
        />
      )}
      <HotelCardLayout
        isAnchor={isAnchor}
        anchorLabelSlot={
          isAnchor && <AnchorLabel onClick={handleHotelCardClick} isMobile />
        }
        isTopOfferMemberDeal={isTopOfferMemberDeal}
        gallerySlot={
          <GalleryWithFetchPriority isPriorityCard={isPriorityCard} />
        }
        propertySummarySlot={
          shouldUseAnchorLink ? (
            <NoStyleLink
              ref={anchorRef}
              href={
                shouldRedirectToAirbnb || (isOTAMode && redirectUrl)
                  ? redirectUrl
                  : accommodationPageURL
              }
              rel="noopener noreferrer"
              target={isEmbedded ? '_self' : '_blank'}
              tabIndex={0}
              onContextMenu={handleAlternativeClick}
              onAuxClick={handleAlternativeClick}
            >
              <PropertySummary hotelId={hotelId} />
            </NoStyleLink>
          ) : (
            <PropertySummary hotelId={hotelId} />
          )
        }
        watchButtonSlot={
          <PriceWatchCTAWrapper
            hotel={hotel}
            ctaComponent={PriceWatchBell}
            componentName="PriceWatchBell"
            searchIsCompleted={!isSearchPending}
          />
        }
        basicPropertyDetailsSlot={basicPropertyDetailsComponent}
        filtersMatchSlot={filtersMatchSlot}
        onClick={handleHotelCardClick}
        offersSlot={
          isDatelessSeoSRP ? (
            <HistoricalPriceOffer
              variant="mobile"
              hotelId={hotel?.objectID}
              price={hotel?.pricing?.minRateInUserCurrency}
            />
          ) : (
            <OfferBox
              withHotelCard
              isLoading={isSearchPending}
              primaryOfferVariant="PrimaryMultiColumn"
              primaryOfferShouldShowTaxation={false}
              offerEntity={offerEntity}
              component={SOURCE_COMPONENT}
              isPrimaryOfferMemberDeal={isTopOfferMemberDeal}
              unavailabilitySlot={unavailabilityComponent}
              shouldRenderUnavailabilitySlot={isAnchor}
              showCashback={cashback.enabled}
              cashbackVariant="short"
              shouldHideSecondaryOffers={isOTAMode}
              dealFreezeBannerSlot={
                shouldShowDealFreezeBanner && (
                  <HotelCardDealFreezeBanner
                    onClick={handleDealFreezeBannerClick}
                  />
                )
              }
              callToBookSlot={
                // Added as part of 27d5ef4a-call-to-book-anchor. The deal freeze banner takes
                // the bottom of the card when both qualify.
                isAnchor &&
                !shouldShowDealFreezeBanner && <CallToBookAnchor isMobile />
              }
              withGreatDealTag
            />
          )
        }
      />
    </>
  )
}

export const ComposedHotelCard = memo(ComposedHotelCardComponent)
